import React, {Fragment, useEffect, useState} from 'react';
import autobind from 'autobind-decorator';
import {Button, Col, Container, FormControl, Row} from 'react-bootstrap';

import {BaseTableColumn} from "./Tables/BaseTableColumn";
import {BaseTableFilter} from "./Tables/BaseTableFilter";
import {ObjectUtils} from "../../Utils";
import {useChangePage} from "../../Hooks/NavigationHooks";
import {Glyphicon} from "./GlyphIcon";
import {PermissionCheckerEnhance} from "./Enhancements";
import {StreamBankLink} from "./StreamBankLink";

import {
    useCheckAuth,
} from "../../Hooks/RouteHooks";
import {
    useMakePageRequest,
} from "../../Hooks/RequestHooks";
import {
    usePermission
} from "../../Hooks/PermissionHooks";

import {useContextIndependentPage} from "../../Hooks/ContextBuilderHooks";
import {useClassNames} from "../../Hooks/StyleHooks";
import {StreamBankTable} from "./Table/StreamBankTable";
import {StreamBankPageHeader} from "./StreamBankPageHeader";

export const ServiceDrivenIndexPage = ((Service) => function ServiceDrivenIndexPage(props) {
    
    const {context = null} = props;
    const {contextNodeId} = context ?? {};
    
    useCheckAuth();
    
    if(!context) {
        useContextIndependentPage();
    }

    const getPageName = () => {
        const pageName = Service.CssClassName;
        return `sb-${pageName}-index-page`;
    };

    const classNames = useClassNames(getPageName());
    const permission = usePermission();
    const makePageRequest = useMakePageRequest();
    const changePage = useChangePage();

    const [data, setData] = useState(null);

    useEffect(() => {
        loadData();
    }, [contextNodeId]);

    const loadData = () => {
        const { serviceParameters } = props;
        
        makePageRequest(
            () => context ? Service.GetWithContext(context) : Service.Get(),
            (x) => {
                setData(x.result);
            }
        );
    };
    
    const onNewClick = props.onNewClick ?? (() => { changePage(`${Service.PageUrl}/new`); });
    const tableFilter = props.tableFilter ?? TableFilter(Service)
    
    return <Container className={classNames}>
        <div>
            <StreamBankPageHeader
                Title={`${Service.ModelNamePlural}`}
            />
            <Row>
                <Col xs={12} md={6}><h2>{`${Service.ModelNamePlural}`}</h2></Col>
                <Col xs={12} md={6}>
                    {
                        permission(Service.CreatePermission) ?
                            <Button variant="link" className="float-right h3" onClick={onNewClick}>
                                <Glyphicon glyph="plus-sign">Add {Service.ModelName}</Glyphicon>
                            </Button>:
                            null
                    }
                </Col>
            </Row>
            <div>
                <hr className="special-hr"/>
            </div>
            <Row>
                <Col xs={12}>
                    <IndexTable
                        FilterComponent={data && data.length > 1 ? tableFilter : null}
                        Data={data}
                        Service={Service}
                        usesContext={!!context}
                    />
                </Col>
            </Row>
        </div>
    </Container>
});

function IndexTable(props) {
    const defaultColumnDefs = [
        {
            title: "Name",
            key: props.Service.TitleProperty,
            sortable: true,
            logic: (x) => x[props.Service.TitleProperty],
            renderer: NameTableColumn(props.Service)
        },
    ];
    
    const primaryKeyProperty = props.Service.PrimaryKeyProperty;
    const columnDefs = props.Service.TableColumns ?? defaultColumnDefs;
    const itemNamePlural = props.Service.ItemNamePlural ?? "items";
    const defaultSortColumn = props.Service.DefaultSortColumn ?? "name";
    const defaultSortAsc = props.Service.DefaultSortAsc ?? true;

    return <StreamBankTable
        columnDefs={columnDefs}
        itemNamePlural={itemNamePlural}
        getKeyForRow={(x) => x[primaryKeyProperty]}
        sortColumn={defaultSortColumn}
        sortAsc={defaultSortAsc}
        {...props}
    />
}

const NameTableColumn = ((Service) => {
    @PermissionCheckerEnhance
    class NameTableColumn extends BaseTableColumn {
        constructor() {
            // TODO: convert ot fucntion ad stop suing HasPermission
            super();
        }

        @autobind
        _render() {
            const primaryKeyValue = this.props?.rowData?.[Service.PrimaryKeyProperty] ?? null;
            if(primaryKeyValue && this.props.HasPermission(Service.UpdatePermission)) {
                return <StreamBankLink
                    className="sb-actionable-link"
                    Href={`${Service.PageUrl}/${this.props.rowData[Service.PrimaryKeyProperty]}`}
                >
                    {this.props.Value}
                </StreamBankLink>;
            } else {
                return <p>{this.props.Value}</p>;
            }
        }
    }

    return NameTableColumn;
});

const TableFilter = ((Service) => class TableFilter extends BaseTableFilter {
    constructor(props) {
        super(props);

        this.setInitialState({
            textFilter: ""
        });
    }

    @autobind
    generateFilters() {
        let filters = [];

        if(this.state.textFilter != "") {
            filters.push({
                code: 'textFilter',
                isSet: true,
                logic: (x) => x[Service.TitleProperty].toUpperCase().includes(this.state.textFilter.toUpperCase())
            });
        }

        this.updateFilters(filters);
    }

    @autobind
    updateTextFilter(event) {
        this.setState({ textFilter: event.target.value }, () => {
            this.generateFilters();
        });
    }

    @autobind
    _render() {
        return <Fragment>
            <Col xs={3} className="d-flex align-items-center">
                <FormControl
                    style={{width: '100%'}}
                    type="text"
                    value={this.state.textFilter}
                    placeholder={`Search ${Service.ModelNamePlural}`}
                    onChange={this.updateTextFilter}
                />
            </Col>
        </Fragment>;
    }
});
